import { useState, type InputHTMLAttributes, type ReactNode, type Ref } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { FieldError, fieldErrorProps } from '@/components/form/FieldError';
import {
  ArrowBackIosNewIcon,
  CheckIcon,
  InfoOutlinedIcon,
  VisibilityIcon,
  VisibilityOffIcon,
} from './AuthIcons';
import styles from './authControls.module.css';

/**
 * The Flutter form controls the auth pages are built from (styles in authControls.module.css):
 *   AuthTextField    PrimaryTextField(inputWithLabel: true) — label, red '*', field, error text
 *   PasswordToggle   the Icons.visibility / visibility_off suffix of the password fields
 *   AuthCheckbox     CustomCheckBox
 *   BackCircleButton the round grey back button (Icons.arrow_back_ios_new)
 *   AuthInfoTooltip  the password rules tooltip beside Create Password
 *   ProgressHud      ModalProgressHUD while the page is busy
 */

interface AuthTextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  id: string;
  label: string;
  /** Flutter isImp: the red '*' after the label */
  required?: boolean;
  /** Flutter widgetBesideImp: shown 20px after the label */
  labelAddon?: ReactNode;
  /** prefixIcon (the country code on Mobile No) */
  startAdornment?: ReactNode;
  /** suffixIcon (the show / hide password eye) */
  endAdornment?: ReactNode;
  /** Already translated message; the field gets Flutter's red error border while it is set */
  error?: string;
  errorId: string;
  inputRef?: Ref<HTMLInputElement>;
}

export function AuthTextField({
  id,
  label,
  required = false,
  labelAddon,
  startAdornment,
  endAdornment,
  error,
  errorId,
  inputRef,
  disabled,
  ...inputProps
}: AuthTextFieldProps) {
  const boxClass = [
    styles.inputBox,
    error ? styles.inputBoxError : '',
    disabled ? styles.inputBoxDisabled : '',
  ].join(' ');

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required && <span className={styles.required} aria-hidden>*</span>}
        {labelAddon && <span className={styles.labelAddon}>{labelAddon}</span>}
      </label>
      <div className={boxClass}>
        {startAdornment && <div className={styles.startAdornment}>{startAdornment}</div>}
        <input
          id={id}
          ref={inputRef}
          className={styles.input}
          disabled={disabled}
          aria-required={required || undefined}
          {...fieldErrorProps(errorId, error)}
          {...inputProps}
        />
        {endAdornment && <div className={styles.endAdornment}>{endAdornment}</div>}
      </div>
      <FieldError id={errorId} message={error} className={styles.fieldError} />
    </div>
  );
}

export function PasswordToggle({ shown, onToggle }: { shown: boolean; onToggle: () => void }) {
  const { t } = useLocalization();
  return (
    <button
      type="button"
      className={styles.eyeBtn}
      onClick={onToggle}
      aria-label={shown ? t('Hide password') : t('Show password')}
      aria-pressed={shown}
    >
      {shown ? <VisibilityIcon size={18} /> : <VisibilityOffIcon size={18} />}
    </button>
  );
}

interface AuthCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

export function AuthCheckbox({ checked, onChange, ...aria }: AuthCheckboxProps) {
  return (
    <span className={styles.checkbox}>
      <input
        type="checkbox"
        className={styles.checkboxInput}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        {...aria}
      />
      <span className={styles.checkboxBox} aria-hidden>
        <CheckIcon size={10} />
      </span>
    </span>
  );
}

export function BackCircleButton({ onClick, className }: { onClick: () => void; className?: string }) {
  const { t } = useLocalization();
  return (
    <button
      type="button"
      className={`${styles.backCircle} ${className ?? ''}`}
      onClick={onClick}
      aria-label={t('Back')}
    >
      <ArrowBackIosNewIcon size={24} />
    </button>
  );
}

/**
 * The (i) beside the Create Password label. Mirrors Flutter's Tooltip on desktop
 * (preferBelow: false; white, 1.5px #EDECEC border, radius 10; green f10n text; Icons.info_outlined
 * 1.w in primaryColor) and ToolTipWithInfoIconWidget on mobile (18px icon, tap to show).
 */
export function AuthInfoTooltip({ message }: { message: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      className={`${styles.infoTip} ${open ? styles.infoTipOpen : ''}`}
      tabIndex={0}
      role="img"
      aria-label={message}
      // preventDefault: it sits in the field's <label>, which would focus the input instead
      onClick={(e) => {
        e.preventDefault();
        setOpen((o) => !o);
      }}
      onBlur={() => setOpen(false)}
    >
      <InfoOutlinedIcon className={styles.infoTipIcon} />
      <span className={styles.infoTipBubble} role="tooltip">{message}</span>
    </span>
  );
}

export function ProgressHud() {
  return (
    <div className={styles.progressHud} role="progressbar" aria-busy="true">
      <div className={styles.spinner} />
    </div>
  );
}

/** Shared button / control classes, for the pages' own buttons */
export const authControlStyles = styles;
